import { useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { PENDING_PREDICTION_KEY, FIRST_TIME_CHOICE_KEY, FIRST_TIME_MODAL_SHOWN_KEY } from "./FirstTimePredictionModal";
import { useToast } from "../contexts/ToastContext";
import { useLanguage } from "../contexts/LanguageContext";
import type { Prediction } from "../types";
import type { Database } from "../lib/database.types";

export function PendingPredictionHandler() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { t: translate } = useLanguage();

  useEffect(() => {
    // Only process if user is logged in
    if (!user) return;

    // Check for pending prediction
    const pendingDataStr = localStorage.getItem(PENDING_PREDICTION_KEY);
    if (!pendingDataStr) return;

    // Small delay to ensure profile is created after signup
    const timer = setTimeout(() => {
      try {
        const pendingData: { matchId: string; prediction: Prediction } = JSON.parse(pendingDataStr);
        const { matchId, prediction } = pendingData;

        if (!matchId || !prediction) {
          throw new Error("Invalid pending prediction data");
        }

        // Save the pending prediction
        savePendingPrediction(user.id, matchId, prediction)
          .then(() => {
            // Clear pending prediction and stored choice
            localStorage.removeItem(PENDING_PREDICTION_KEY);
            localStorage.removeItem(FIRST_TIME_CHOICE_KEY);
            // Mark modal as shown so it doesn't appear again after signup
            localStorage.setItem(FIRST_TIME_MODAL_SHOWN_KEY, "true");
            showToast(translate("firstTime.predictionSaved"), "success");
          })
          .catch((error) => {
            console.error("Failed to save pending prediction:", error);
            // Don't show error to user, just log it
          });
      } catch (error) {
        console.error("Failed to parse pending prediction:", error);
        localStorage.removeItem(PENDING_PREDICTION_KEY);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [user, showToast, translate]);

  return null;
}

async function savePendingPrediction(
  userId: string,
  matchId: string,
  prediction: Prediction
): Promise<void> {
  const { supabase } = await import("../lib/supabase");

  // Check if prediction already exists
  const { data: existing } = await supabase
    .from("predictions")
    .select("id")
    .eq("user_id", userId)
    .eq("match_id", matchId)
    .maybeSingle();

  const predictionData = {
    user_id: userId,
    match_id: matchId,
    home_score: prediction.homeScore,
    away_score: prediction.awayScore,
    mood: prediction.mood,
  };

  if (existing) {
    // Update existing prediction
    const { error: updateError } = await supabase
      .from("predictions")
      .update(predictionData)
      .eq("id", existing.id);
    
    if (updateError) throw updateError;
  } else {
    // Insert new prediction
    const { error } = await supabase
      .from("predictions")
      .insert(predictionData)
      .select()
      .single();

    if (error) throw error;

    // Update user's prediction count
    await supabase.rpc("increment_predictions", { user_id: userId });

    // Update achievement progress
    await updateAchievementProgress(userId, prediction);
  }
}

async function updateAchievementProgress(
  userId: string,
  prediction: Prediction
): Promise<void> {
  const { supabase } = await import("../lib/supabase");

  try {
    // Get current profile data
    const { data: profile } = await supabase
      .from("profiles")
      .select("bold_predictions, last_prediction_date, prediction_streak")
      .eq("id", userId)
      .single();

    if (!profile) return;

    const updates: Partial<Database["public"]["Tables"]["profiles"]["Update"]> = {};

    // Track bold predictions
    if (prediction.mood === "bold") {
      updates.bold_predictions = (profile.bold_predictions ?? 0) + 1;
    }

    // Track prediction streak
    const today = new Date().toISOString().split("T")[0];
    const lastDate = profile.last_prediction_date;
    const yesterday = new Date(Date.now() - 86400000)
      .toISOString()
      .split("T")[0];

    if (lastDate === yesterday) {
      // Continue streak
      updates.prediction_streak = (profile.prediction_streak ?? 0) + 1;
    } else if (lastDate !== today) {
      // Start new streak
      updates.prediction_streak = 1;
    }
    // If lastDate === today, don't update streak (already predicted today)

    updates.last_prediction_date = today;

    // Apply updates
    if (Object.keys(updates).length > 0) {
      await supabase.from("profiles").update(updates).eq("id", userId);
    }
  } catch (error) {
    console.error("Failed to update achievement progress:", error);
  }
}

